import React, { useCallback, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import fileDownload from 'react-file-download';
import dayjs from '~/timeInit';

import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import UserSearchWidget from '~/components/Common/UserSearchWidget';

import { toPromoBrand } from '~/consts/promoBrand';

import { useBulkUpdateUnusedVoucherUsersExpiry } from '~/hooks/apis/promo/useBulkUpdateUnusedVoucherUsersExpiry';
import { useCreateVoucherUsers } from '~/hooks/apis/promo/useCreateVoucherUsers';
import { allVoucherUsersQueryKeys } from '~/hooks/apis/promo/useListAllVoucherUsers';
import useCurrentBrand from '~/hooks/useCurrentBrand';

import { listVoucherUsers } from '~/services/PromoService';

import { ConvertToCSV } from '~/utils/arrayUtils';
import { getDefaultAddVoucherUsersExpiryDate, normalizeToISODate } from '~/utils/dateFormatter';
import DateFormatterDetailed from '~/utils/dateFormatterDetails';

import PageSubheader from '../Common/Elements/PageSubheader';
import DebugModal from '../DebugModal/DebugModal';
import Accordion from '../v2/common/Accordion/Accordion';

import BulkUploadVoucherUsersDialog from './BulkUploadVoucherUsersDialog';
import VoucherUsersSearchPage from './VoucherUsersSearchPage';

type Props = {
  userIds: Array<string>;
  onChange: (userIds: Array<string>) => void;
  expiredAt: string;
  onExpiredAtChange: (expiredAt: string) => void;
  /** Voucher form fields — used when resolving empty add-user expiry (same rules as initial default). */
  voucherUserExpiresAt?: string;
  voucherUserExpiresInDays?: string;
  disabled?: boolean;
  voucherId?: string;
  /** Exact voucher name for bulk expiry API (matches saved voucher name). */
  voucherName?: string;
  showAdvanceDescription?: boolean;
  /** Lifted user filter for promo requests when embedded lookup is on voucher edit (avoids duplicate Recent Requests). */
  promoRequestsUserFilter?: string;
  onPromoRequestsUserFilterChange?: (userId: string) => void;
};

export default function VoucherUsers({
  userIds,
  onChange,
  expiredAt,
  onExpiredAtChange,
  voucherUserExpiresAt = '',
  voucherUserExpiresInDays = '',
  showAdvanceDescription,
  disabled = false,
  voucherId,
  voucherName = '',
  promoRequestsUserFilter,
  onPromoRequestsUserFilterChange,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const brand = useCurrentBrand();
  const promoBrand = toPromoBrand(brand);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userSearchValue, setUserSearchValue] = useState('');
  const [allowVoucherReuse, setAllowVoucherReuse] = useState(false);
  const [reactivateExpiredVouchers, setReactivateExpiredVouchers] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [bulkExpiryConfirmOpen, setBulkExpiryConfirmOpen] = useState(false);

  const voucherUsersPageDebugData = useMemo(
    () => ({
      voucherId,
      voucherName: voucherName.trim() || undefined,
      brand: brand ?? undefined,
    }),
    [brand, voucherId, voucherName],
  );

  const createVoucherUsersMutation = useCreateVoucherUsers();
  const bulkUpdateUnusedExpiryMutation = useBulkUpdateUnusedVoucherUsersExpiry();

  /** Same rules as adding users: Expires At if set, else voucher user expires at / expires in days. */
  const resolvedVoucherUserExpiryIso = useMemo(
    () =>
      expiredAt.trim()
        ? normalizeToISODate(expiredAt)
        : normalizeToISODate(
            getDefaultAddVoucherUsersExpiryDate({
              voucherUserExpiresAt,
              voucherUserExpiresInDays,
            }),
          ),
    [expiredAt, voucherUserExpiresAt, voucherUserExpiresInDays],
  );

  const resolvedVoucherUserExpiryDisplay = useMemo(
    () => dayjs(resolvedVoucherUserExpiryIso).format('YYYY-MM-DD'),
    [resolvedVoucherUserExpiryIso],
  );

  const handleAdd = useCallback(async () => {
    if (!selectedUserId) {
      enqueueSnackbar('Select a user to add', { variant: 'error' });
      return;
    }

    if (!voucherId) {
      onChange([...userIds, selectedUserId]);
      setSelectedUserId('');
      setUserSearchValue('');
      return;
    }

    try {
      const response = await createVoucherUsersMutation.mutateAsync({
        voucher_id: voucherId,
        expired_at: resolvedVoucherUserExpiryIso,
        allow_voucher_reuse: allowVoucherReuse,
        reactivate_expired_vouchers: reactivateExpiredVouchers,
        voucher_users: [{ user_id: selectedUserId }],
      });
      const r = response.result;
      const resultWide = r as {
        createdVoucherUsers?: Array<unknown>;
        updatedVoucherUsers?: Array<unknown>;
        reactivatedVoucherUsers?: Array<unknown>;
        skippedVoucherUsers?: Array<unknown>;
        voucherUsers?: Array<unknown>;
      };
      const anySuccess =
        (resultWide.createdVoucherUsers?.length ?? 0) > 0 ||
        (resultWide.updatedVoucherUsers?.length ?? 0) > 0 ||
        (resultWide.reactivatedVoucherUsers?.length ?? 0) > 0 ||
        (resultWide.voucherUsers?.length ?? 0) > 0;
      const anySkipped = (resultWide.skippedVoucherUsers?.length ?? 0) > 0;
      const shouldClearForm = anySuccess || !anySkipped;

      if (r?.skippedVoucherUsers?.length) {
        const reason = r.skippedVoucherUsers[0].reason === 'already_redeemed' ? 'already redeemed' : 'expired unused';
        enqueueSnackbar(`User was skipped — ${reason}`, { variant: 'warning' });
      } else if (r?.reactivatedVoucherUsers?.length) {
        enqueueSnackbar('Reactivated expired voucher user', { variant: 'success' });
      } else if (r?.updatedVoucherUsers?.length) {
        enqueueSnackbar('Updated existing voucher user (reuse)', { variant: 'success' });
      } else {
        enqueueSnackbar('Added voucher user', { variant: 'success' });
      }
      queryClient.invalidateQueries({ queryKey: ['voucher-users', voucherId] });
      queryClient.invalidateQueries({ queryKey: allVoucherUsersQueryKeys.all });
      if (shouldClearForm) {
        setSelectedUserId('');
        setUserSearchValue('');
      }
    } catch (error) {
      enqueueSnackbar(`Failed to add voucher user: ${(error as Error).message}`, { variant: 'error' });
    }
  }, [
    allowVoucherReuse,
    createVoucherUsersMutation,
    enqueueSnackbar,
    onChange,
    queryClient,
    reactivateExpiredVouchers,
    selectedUserId,
    userIds,
    voucherId,
    resolvedVoucherUserExpiryIso,
  ]);

  // Handle form submit (Enter key) to add user when a user is selected
  const handleFormSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (selectedUserId && !disabled && !createVoucherUsersMutation.isPending) {
        handleAdd();
      }
    },
    [selectedUserId, disabled, createVoucherUsersMutation.isPending, handleAdd],
  );

  const handleDownloadCsv = useCallback(async () => {
    if (!voucherId) {
      return;
    }

    setIsDownloading(true);
    try {
      const PAGE_SIZE = 200;
      let page = 0;
      let allUsers: Array<definitions['VoucherUser']> = [];
      let hasMore = true;

      while (hasMore) {
        const response = await listVoucherUsers(voucherId, {
          page,
          limit: PAGE_SIZE,
          includeExpired: true,
          includeUsed: true,
          ...(promoBrand ? { brand: promoBrand } : {}),
        });
        const users = response.result?.voucherUsers ?? [];
        allUsers = allUsers.concat(users);
        const total = response.result?.total ?? 0;
        hasMore = users.length > 0 && allUsers.length < total;
        page += 1;
      }

      const csvData = allUsers.map((user) => ({
        user_id: user.userId,
        created_at: user.createdAt ?? '',
        used_at: user.usedAt ?? '',
        expired_at: user.expiredAt ?? '',
      }));

      const csv = ConvertToCSV(csvData);
      fileDownload(csv, `voucher-users-${voucherId}.csv`);
      enqueueSnackbar(`Downloaded ${allUsers.length} voucher users`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to download voucher users: ${(error as Error).message}`, { variant: 'error' });
    } finally {
      setIsDownloading(false);
    }
  }, [promoBrand, voucherId, enqueueSnackbar]);

  const handleBulkExpiryOpen = useCallback(() => {
    const trimmedName = voucherName.trim();
    if (!trimmedName) {
      enqueueSnackbar('Set a voucher name before bulk-updating expiry', { variant: 'error' });
      return;
    }
    if (!brand) {
      enqueueSnackbar('Select a brand (tenant) before bulk-updating expiry', { variant: 'error' });
      return;
    }
    setBulkExpiryConfirmOpen(true);
  }, [brand, enqueueSnackbar, voucherName]);

  const handleBulkExpiryConfirm = useCallback(async () => {
    const trimmedName = voucherName.trim();
    if (!trimmedName || !voucherId || !brand) {
      setBulkExpiryConfirmOpen(false);
      return;
    }
    try {
      const response = await bulkUpdateUnusedExpiryMutation.mutateAsync({
        voucherName: trimmedName,
        expiredAt: resolvedVoucherUserExpiryIso,
        brand: brand as definitions['Promo Brands'],
      });
      const n = response.result?.updatedCount ?? 0;
      enqueueSnackbar(response.message || `Updated ${n} unused voucher user row(s)`, { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['voucher-users', voucherId] });
      queryClient.invalidateQueries({ queryKey: allVoucherUsersQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['voucher', voucherId] });
    } catch (error) {
      enqueueSnackbar(`Bulk update failed: ${(error as Error).message}`, { variant: 'error' });
    } finally {
      setBulkExpiryConfirmOpen(false);
    }
  }, [
    bulkUpdateUnusedExpiryMutation,
    brand,
    enqueueSnackbar,
    queryClient,
    resolvedVoucherUserExpiryIso,
    voucherId,
    voucherName,
  ]);

  const handleBulkExpiryDialogClose = useCallback(
    (_event: object, _reason: 'backdropClick' | 'escapeKeyDown') => {
      if (bulkUpdateUnusedExpiryMutation.isPending) {
        return;
      }
      setBulkExpiryConfirmOpen(false);
    },
    [bulkUpdateUnusedExpiryMutation.isPending],
  );

  return (
    <Box id="users">
      <PageSubheader title="Users">
        {showAdvanceDescription && (
          <Box component="span" color="text.secondary" alignItems="left">
            Users that qualify for this voucher. The voucher is not editable if it has users associated.
          </Box>
        )}
      </PageSubheader>
      {!voucherId ? (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Create the voucher to add users.
        </Typography>
      ) : (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Add Voucher Users
          </Typography>
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
            User expiry
          </Typography>
          <RadioGroup
            row
            value={expiredAt.trim() ? 'date' : 'voucher'}
            onChange={(e) => {
              const next = e.target.value;
              if (next === 'voucher') {
                onExpiredAtChange('');
              } else {
                onExpiredAtChange(
                  getDefaultAddVoucherUsersExpiryDate({
                    voucherUserExpiresAt,
                    voucherUserExpiresInDays,
                  }),
                );
              }
            }}
            sx={{ mb: 1 }}
          >
            <FormControlLabel value="voucher" control={<Radio />} label="Voucher Default" disabled={disabled} />
            <FormControlLabel value="date" control={<Radio />} label="Expiry Override" disabled={disabled} />
          </RadioGroup>
          {expiredAt.trim() ? (
            <TextField
              label="Expires At"
              type="date"
              value={expiredAt}
              onChange={(event) => onExpiredAtChange(event.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={disabled}
              sx={{ minWidth: 240, mb: 2 }}
              helperText={
                <Box>
                  <Typography variant="caption" color="text.secondary" component="div">
                    Override expiry for users added below (bulk actions use the same resolved date).
                  </Typography>
                  <DateFormatterDetailed date={normalizeToISODate(expiredAt)} highlightIfPast includeYear />
                </Box>
              }
            />
          ) : (
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                gap: 0.5,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Uses voucher expiry settings — currently
              </Typography>
              <DateFormatterDetailed date={resolvedVoucherUserExpiryIso} highlightIfPast includeYear />
              <Typography variant="body2" color="text.secondary">
                when adding users.
              </Typography>
            </Box>
          )}
          <form onSubmit={handleFormSubmit}>
            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
              <Box sx={{ flex: 1 }}>
                <UserSearchWidget
                  id="voucher-user-search"
                  label="Name / Email / Customer Support Code / User ID"
                  formErrors={[]}
                  onChange={(_, value) => setSelectedUserId(value)}
                  passedAutocompleteValue={userSearchValue}
                  setPassedAutocompleteValue={setUserSearchValue}
                  disabled={disabled}
                />
              </Box>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                disabled={disabled || createVoucherUsersMutation.isPending}
              >
                Add users
              </Button>
            </Stack>
          </form>
          <Stack direction="row" spacing={2} mb={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allowVoucherReuse}
                  onChange={(e) => setAllowVoucherReuse(e.target.checked)}
                  disabled={disabled}
                />
              }
              label="Allow voucher reuse"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={reactivateExpiredVouchers}
                  onChange={(e) => setReactivateExpiredVouchers(e.target.checked)}
                  disabled={disabled}
                />
              }
              label="Reactivate expired vouchers"
            />
          </Stack>
          <Stack direction="row" spacing={2} mb={0.5}>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => setIsBulkUploadOpen(true)}
              disabled={disabled}
            >
              Bulk Upload CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadCsv}
              disabled={isDownloading || !voucherId}
            >
              {isDownloading ? 'Downloading...' : 'Download CSV'}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Exports every voucher user row for this voucher (not limited to the table filters).
          </Typography>
        </>
      )}

      {voucherId && (
        <Box mb={3} mt={3}>
          <VoucherUsersSearchPage
            voucherId={voucherId}
            defaultIncludeExpired
            hideFooterDebug
            showExpireVoucherUserActions
            expireVoucherUserActionsDisabled={disabled}
            promoRequestsUserFilter={promoRequestsUserFilter}
            onPromoRequestsUserFilterChange={onPromoRequestsUserFilterChange}
          />
          <Accordion
            title={`Bulk update unused users' expiry (aka "Extend this voucher")`}
            columns={1}
            defaultExpanded={false}
          >
            <Box mt={2} mb={1}>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
                Applies the same expiry as adding users—&quot;Expiry date&quot; above if selected, otherwise voucher
                user expiry from voucher settings—to all unused (not redeemed) voucher user rows for this voucher name.
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <TextField
                  label="Expiry applied"
                  type="date"
                  value={resolvedVoucherUserExpiryDisplay}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                  disabled={disabled || !voucherName.trim() || !brand}
                  sx={{ minWidth: 240 }}
                  helperText={
                    <Box>
                      <Typography variant="caption" color="text.secondary" component="div">
                        Edit Assign user expiry or voucher expiry fields above to change
                      </Typography>
                      <DateFormatterDetailed date={resolvedVoucherUserExpiryIso} highlightIfPast includeYear />
                    </Box>
                  }
                />
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<EditCalendarIcon />}
                  onClick={handleBulkExpiryOpen}
                  disabled={disabled || bulkUpdateUnusedExpiryMutation.isPending || !voucherName.trim() || !brand}
                >
                  Apply to all unused
                </Button>
              </Stack>
            </Box>
          </Accordion>
        </Box>
      )}

      <Dialog
        open={bulkExpiryConfirmOpen}
        onClose={handleBulkExpiryDialogClose}
        disableEscapeKeyDown={bulkUpdateUnusedExpiryMutation.isPending}
      >
        <DialogTitle>Bulk update unused voucher users</DialogTitle>
        <DialogContent>
          <Typography variant="body2" component="div" gutterBottom>
            Set expiry to:
          </Typography>
          {resolvedVoucherUserExpiryIso ? (
            <Box sx={{ mb: 1 }}>
              <DateFormatterDetailed date={resolvedVoucherUserExpiryIso} highlightIfPast includeYear />
            </Box>
          ) : (
            <Typography variant="body2" component="div" gutterBottom>
              —
            </Typography>
          )}
          <Typography variant="body2" component="div">
            (UTC midnight when given as a date only) for every <strong>unused</strong> voucher user row matching voucher
            name &quot;{voucherName.trim() || '—'}&quot;? This does not change redeemed rows.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => setBulkExpiryConfirmOpen(false)}
            disabled={bulkUpdateUnusedExpiryMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleBulkExpiryConfirm}
            disabled={bulkUpdateUnusedExpiryMutation.isPending}
          >
            Update expiry
          </Button>
        </DialogActions>
      </Dialog>

      {voucherId && (
        <BulkUploadVoucherUsersDialog
          open={isBulkUploadOpen}
          onClose={() => setIsBulkUploadOpen(false)}
          voucherId={voucherId}
          brand={brand}
        />
      )}
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <DebugModal type="generic" title="Voucher users page debug" data={voucherUsersPageDebugData} />
      </Box>
    </Box>
  );
}
